// External libraries
import React, { useMemo, useState } from 'react';
import { Alert } from '@paypalcorp/pp-react-alert-weasley-v7';
import {
  Address as AddressComponent,
  FloatingLabelSelect,
  FullName,
  // @ts-expect-error untyped-import
} from '@checkout/react-components';

// Internal modules
import { useStoreState } from '@/store';
import { PaypalThemedProvider } from '@/styles';
import {
  CI,
  features,
  getAddressMetaKey,
  getContent,
  getGlobals,
  getLocale,
  useEndCiWith,
} from '@/lib';
import { LoadingElement, LoadingFields } from '@/components/Common';
import {
  getAddressFieldsChangeHandler,
  getAddressSelectionHandler,
  getResetHandler,
  getShippingCountryChangeHandler,
  getSncAddressFieldsBlurHandler,
  getSntAddressFieldsBlurHandler,
  // @ts-expect-error untyped-import
} from './handlers';
import {
  canPayerShipToBilling,
  generateChangeHandlerMeta,
  getAdditionalCountryProps,
  hideAddressSuggestionSingleLine,
  updateAddressMetadataForSingleLine,
  // @ts-expect-error untyped-import
} from './utils';
import { getAddressMetadata } from '@/lib/utils';
import {
  addressHeaderStyles,
  countryContainerStyleOverrides,
  getFieldsetStyles,
} from './styles';
import { SplitAddressLine1 } from './SplitAddressLine1';
// @ts-expect-error untyped-import
import { AddressAutoPopulationPostalCode } from './AddressAutoPopulationPostalCode';
import type {
  GenericObjectType,
  InputOnBlurChange,
  InputOnChange,
  InputOnFocus,
} from '@/types';
import type { FormState } from '@/store/models/form/types';
import type { AddressType } from '@/types';

const {
  device: { isMobile },
} = getGlobals();

type CountryProps = {
  blurHandler?: InputOnBlurChange;
  changeHandler: InputOnChange;
  country?: {
    displayError?: boolean;
    error?: string;
    errorCode?: string;
    value?: string;
  };
  focusHandler?: InputOnFocus;
  isChangeBlocked?: boolean;
  selectOptions?: GenericObjectType[];
};

const Country = (props: CountryProps): JSX.Element => {
  if (!props.selectOptions?.length) {
    return <LoadingElement />;
  }

  return (
    <FloatingLabelSelect
      floatingLabelContainerStyleOverrides={
        props.isChangeBlocked && countryContainerStyleOverrides
      }
      isRequired={true}
      label={getContent('fieldLabels.country')}
      {...props}
    />
  );
};

type BlockNonDomesticShippingProps = {
  hasBlockNonDomesticShippingRestriction: boolean;
  isShipping: boolean;
  merchantCountry: boolean | string;
};

const BlockNonDomesticShipping: React.FC<BlockNonDomesticShippingProps> = ({
  hasBlockNonDomesticShippingRestriction,
  isShipping,
  merchantCountry,
}) => {
  if (
    !isShipping ||
    !hasBlockNonDomesticShippingRestriction ||
    !merchantCountry
  ) {
    return null;
  }

  return (
    <PaypalThemedProvider>
      <Alert id="shipping-info-message" type="info">
        {getContent('messages.blockNonDomesticShipping', {
          merchantCountry,
        })}
      </Alert>
    </PaypalThemedProvider>
  );
};

type CountryAndNameProps = {
  blurHandler?: InputOnBlurChange;
  changeHandler: InputOnChange;
  // @ts-expect-error - Add country typing to FormState
  country?: FormState['country'];
  disabled?: boolean;
  firstName?: FormState['firstName'];
  focusHandler?: InputOnFocus;
  lastName?: FormState['lastName'];
  onShippingCallback: () => void;
  showFullName?: boolean;
  addressType: AddressType;
};

const CountryAndName = (props: CountryAndNameProps): JSX.Element => {
  const { showFullName = true, onShippingCallback } = props;
  const billingFirstName = useStoreState(({ form }) => form.firstName);
  const billingLastName = useStoreState(({ form }) => form.lastName);
  const countriesList = useStoreState(({ api }) => {
    const { locale } = getLocale();
    return api.griffinMetadata.territories[locale];
  });
  const merchantInfo = useStoreState(({ api }) => api.cartDetails.merchant);
  const isShipping = props.addressType === 'shipping';
  const hasBlockNonDomesticShippingRestriction = features.isActive(
    'blockNonDomesticShipping',
  );
  const theme = features.get('rebrandFormFieldsTheme');
  const merchantCountry =
    merchantInfo?.country &&
    hasBlockNonDomesticShippingRestriction &&
    getContent(`country.${merchantInfo.country}`);

  return (
    <>
      <BlockNonDomesticShipping
        isShipping={isShipping}
        hasBlockNonDomesticShippingRestriction={
          hasBlockNonDomesticShippingRestriction
        }
        merchantCountry={merchantCountry}
      />

      {isShipping && (
        <Country
          changeHandler={getShippingCountryChangeHandler(
            props,
            onShippingCallback,
          )}
          disabled={props.disabled || hasBlockNonDomesticShippingRestriction}
          isChangeBlocked={hasBlockNonDomesticShippingRestriction}
          selectOptions={countriesList}
          {...(hasBlockNonDomesticShippingRestriction && {
            'aria-describedby': 'shipping-info-message',
          })}
          {...getAdditionalCountryProps(props)}
        />
      )}

      {showFullName &&
        !features.isActive('shouldDisplayJpKanaAndKanjiNames') && (
          <FullName
            blurHandler={props.blurHandler}
            changeHandler={props.changeHandler}
            firstName={isShipping ? props.firstName : billingFirstName}
            focusHandler={props.focusHandler}
            labels={{
              firstName: getContent('fieldLabels.firstName'),
              lastName: getContent('fieldLabels.lastName'),
            }}
            lastName={isShipping ? props.lastName : billingLastName}
            {...(isShipping && {
              firstNameId: 'shippingFirstName',
              firstNameInputName: 'shippingFirstName',
              generateChangeHandlerMeta,
              lastNameId: 'shippingLastName',
              lastNameInputName: 'shippingLastName',
            })}
            theme={theme}
          />
        )}
    </>
  );
};

type AddressFieldsProps = {
  blurHandler?: InputOnBlurChange;
  changeHandler: InputOnChange;
  focusHandler?: InputOnFocus;
  onShippingCallback: () => void;
  showTitle?: boolean;
  addressType: AddressType;
};

const AddressFields = ({
  showTitle = true,
  onShippingCallback,
  ...props
}: AddressFieldsProps): JSX.Element => {
  useEndCiWith(CI.Event.SAME_AS_SHIPPING_ADDRESS_RENDER, CI.Status.Ok);

  const { addressType } = props;
  const isBilling = addressType === 'billing';
  const typeOfAddress = !isBilling ? 'shippingAddress' : 'billingAddress';
  const {
    autoCompleteType,
    // @ts-expect-error firstName is not defined in store on billingAddress
    firstName: addressFirstName,
    houseNumber,
    isManipulated,
    // @ts-expect-error lastName is not defined in store on billingAddress
    lastName: addressLastName,
    streetName,
    ...address
  } = useStoreState(({ form }) => form[typeOfAddress]);

  // checking for new shipping address quality single line experiment
  const hasShippingAddressQualityExperiment = features.isActive(
    'hasShippingAddressQualityExperiment',
  );
  const hasAddressAutoCompleteSingleLine = features.isActive(
    'hasAddressAutoCompleteSingleLine',
  );
  const isBrowserAutofillSuppressed = features.isActive(
    'hasAddressAutocompleteWithNoBrowserAutofill',
  );
  const theme = features.get('rebrandFormFieldsTheme');
  const addressMetadata = useStoreState(
    ({ api }) => api.griffinMetadata.address,
  );
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const hasShipToBillingCheckbox =
    canPayerShipToBilling() && features.isActive('isAllowChangeShipping');
  const { country, locale } = getLocale();
  // @ts-expect-error country is not always defined in store
  const { value: shippingCountry } = address.country || {};
  const addressMetaKey = getAddressMetaKey(shippingCountry || country);
  const [suggestedAddressList, setSuggestedAddressList] = useState([]);
  const [showAutoCompleteSingleLine, setShowAutoCompleteSingleLine] = useState(
    hasAddressAutoCompleteSingleLine,
  );
  // hide single line if single line treatment is on and also
  // cart has prefilled address
  const hideSingleLine =
    showAutoCompleteSingleLine && hideAddressSuggestionSingleLine(address);
  const enableAddressAutocomplete = features.isActive('hasAddressAutocomplete');
  const shouldShowSplitLineOne = features.isActive('shouldShowSplitLineOne');
  const isSntSupported = features.isActive('isSntSupported');
  const isShippingCallbacksEligible = features.someActive(
    'isShippingCallbacksV2Eligible',
    'isShippingCallbackV1Eligible',
  );
  let additionalProps = {};
  if (enableAddressAutocomplete) {
    // additionalProps overrides changeHandler on props
    additionalProps = {
      addressSelectionHandler: getAddressSelectionHandler({
        onShippingCallback,
        setShowAutoCompleteSingleLine,
        setSuggestedAddressList,
        suggestedAddressList,
      }),
      changeHandler: getAddressFieldsChangeHandler({
        country: shippingCountry || country,
        props,
        setShowAutoCompleteSingleLine,
        setSuggestedAddressList,
        suppressBrowserAutofill: isBrowserAutofillSuppressed,
      }),
      contentObject: {
        addressSharedDisclaimer:
          features.isActive('hasAddressAutocompleteDisclaimer') &&
          getContent('terms.addressSharedWithGoogle'),
        line1Instruction: getContent('messages.startTypingAddressLine1'),
        privacyLink:
          features.isActive('hasAddressAutocompletePrivacyLink') &&
          `https://policies.google.com/privacy?hl=${locale?.slice(0, 2)}`,
        privacyLinkText:
          features.isActive('hasAddressAutocompletePrivacyLink') &&
          getContent('compliance.googlePrivacyPolicyLinkText'),
      },
      enableAddressAutocomplete,
      isMobile,
      line1: {
        ...address?.line1,
        suppressBrowserAutofill: isBrowserAutofillSuppressed,
      },
      resetHandler: getResetHandler({
        setSuggestedAddressList,
      }),
      suggestedAddressList,
    };
  }

  additionalProps = {
    changeHandler: props.changeHandler,
    focusHandler: props.focusHandler,
    ...additionalProps,
    blurHandler: (e: React.FocusEvent<HTMLInputElement>) => {
      if (isShippingCallbacksEligible) {
        getSncAddressFieldsBlurHandler(e, onShippingCallback);
      }
      if (isSntSupported) {
        getSntAddressFieldsBlurHandler(e);
      }
    },
  };

  /* addressType isn't recognized by the Address component, so we need to include it here */
  const SplitAddressLine1WithAddressType = useMemo(
    () =>
      (props: any): JSX.Element => {
        return <SplitAddressLine1 {...props} addressType={addressType} />;
      },
    [addressType],
  );

  const componentsMapOverrides = {
    ...(shouldShowSplitLineOne && {
      line1: SplitAddressLine1WithAddressType,
      postalCode: AddressAutoPopulationPostalCode,
    }),
  };

  const line1Content = getContent(`billing.${addressType}Address`);
  // if singleline treatment on and true
  // show line1 label as billing or shipping address
  // else show as-is from griffin
  const localeAddressMetadata =
    enableAddressAutocomplete && hideSingleLine
      ? updateAddressMetadataForSingleLine(
          getAddressMetadata(addressMetadata, addressMetaKey),
          line1Content,
        )
      : getAddressMetadata(addressMetadata, addressMetaKey);

  return (
    <AddressComponent
      addressMetadata={localeAddressMetadata}
      componentsMapOverrides={componentsMapOverrides}
      disableAll={isFormSubmitting}
      enableAddressAutocompleteSingleLine={hideSingleLine}
      enableDontSeeMyAddressSuggestion={hasAddressAutoCompleteSingleLine}
      fieldsPrefix={addressType}
      generateChangeHandlerMeta={generateChangeHandlerMeta}
      legend={showTitle && line1Content}
      loadingRenderProp={() => <LoadingFields />}
      overrideAddressSuggestionStyle={hasShippingAddressQualityExperiment}
      renderBefore={() => (
        <CountryAndName
          // @ts-expect-error country is not always defined in store
          country={address.country}
          disabled={isFormSubmitting}
          firstName={addressFirstName}
          lastName={addressLastName}
          onShippingCallback={onShippingCallback}
          {...props}
        />
      )}
      styleOverrides={{
        fieldset: getFieldsetStyles({
          hasShipToBillingCheckbox,
          isBilling,
        }),
        legend: addressHeaderStyles,
      }}
      theme={theme}
      {...address}
      {...additionalProps}
    />
  );
};

export { AddressFields, Country, CountryAndName };
